import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { TableSortLabel, Typography } from "@mui/material";
import React, { useState } from "react";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";

interface Props {
  tableHeadings: { [key: string]: string };
  newSearchTerms: any;
}

const NewSearchTerms = ({ tableHeadings, newSearchTerms }: Props) => {
  const taskDetails = useSelector(
    (state: any) => state.businessInsights.taskDetails
  );
  const [sortedData, setSortedData] = React.useState<any[]>([]);
  const [sort, setSort] = useState<{
    keytoSort: string | undefined;
    direction: "asc" | "desc" | undefined;
  }>({ keytoSort: "search_term", direction: "desc" });
  const TCPA = taskDetails?.data?.["query_output:new_tcpa"]?.value || 0;

  const tableheaders = [
    {
      id: 0,
      KEY: "search_term",
      label: "Search Term",
      sortable: true,
    },
    { id: 1, KEY: "kw_name", label: "Keyword", sortable: true },
    {
      id: 2,
      KEY: "matchtype",
      label: "Match Type",
      sortable: true,
      minWidth: 120,
    },
    { id: 3, KEY: "spend", label: "Spend", sortable: true },
    { id: 4, KEY: "CAC", label: "CAC", sortable: true },
    { id: 5, KEY: "ftd", label: "FTD", sortable: true },
    { id: 6, KEY: "cpc", label: "CPC", sortable: true },
    { id: 7, KEY: "clicks", label: "Clicks", sortable: true },
  ];

  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }

        let aValue, bValue;

        aValue = a[keytoSort];
        bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );
  const sortedRows = getSortedData(newSearchTerms?.query_output || []);

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return;

    setSort({
      keytoSort: header.KEY,
      direction:
        header.KEY === sort.keytoSort && sort.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  const formatCurrency = (value: number) => {
    return `${value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}$`;
  };

  const formatDecimal = (value: number) => {
    return value.toFixed(2);
  };

  React.useEffect(() => {
    if (newSearchTerms?.query_output) {
      const sorted = [...newSearchTerms.query_output].sort(
        (a, b) => b.spend - a.spend
      );
      setSortedData(sorted);
    }
  }, [newSearchTerms]);

  const headCells = Object.entries(tableHeadings);

  const redBoxStyle = {
    bgcolor: "rgba(253, 237, 237, 1)",
    padding: "3px",
    borderRadius: 1,
    border: "1px solid rgba(211, 47, 47, 0.5)",
    display: "flex",
    alignItems: "left",
    justifyContent: "left",
    height: "26px",
    width: "fit-content",
  };

  const greenBoxStyle = {
    bgcolor: "rgba(237, 247, 237, 1)",
    padding: "3px",
    borderRadius: 1,
    border: "1px solid rgba(46, 125, 50, 0.5)",
    display: "flex",
    alignItems: "left",
    justifyContent: "left",
    marginTop: "4px",
    height: "26px",
    width: "fit-content",
  };

  const getHighlightStyle = (key: string, value: number, _index: number) => {
    if (key === "spend" || key === "cpc") {
      const sortedValues = sortedData
        .map((row) => row[key])
        .sort((a, b) => b - a);
      if (sortedValues.indexOf(value) < 3) return redBoxStyle;
      if (sortedValues.indexOf(value) >= sortedValues.length - 3)
        return greenBoxStyle;
    }
    if (key === "CAC" && TCPA && value > TCPA * 1.4) return redBoxStyle;
    if (key === "ftd" && value === 0) return redBoxStyle;
    return {};
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined || value === "") return "";
    return value;
  };

  // height for body scrolling area
  const bodyScrollHeight = 260;

  return (
    <>
      <Paper sx={{ marginTop: "16px" }}>
        <Typography fontSize={24} sx={{ padding: "8px 16px" }}>
          New Search Terms
        </Typography>

        {/* TableContainer - no scrolling here */}
        <TableContainer
          className="PageScantablewrap"
          sx={{ overflow: "visible", paddingBottom: "16px" }}
        >
          {/* HEADER TABLE */}
          <Table
            aria-label="collapsible table head"
            size="small"
            sx={{ tableLayout: "fixed", width: "100%" }}
          >
            <colgroup>
              {tableheaders.map((h) => (
                <col key={h.id} style={{ minWidth: h.minWidth ?? 120 }} />
              ))}
            </colgroup>

            <TableHead>
              <TableRow>
                {tableheaders.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "background.paper",
                      zIndex: 3,
                      whiteSpace: "nowrap", // keep header text single-line
                    }}
                  >
                    <TableSortLabel
                      active={sort.keytoSort === header.KEY}
                      direction={
                        sort.keytoSort === header.KEY ? sort.direction : "asc"
                      }
                      onClick={() => handleHeaderClick(header)}
                      disabled={!header.sortable}
                    >
                      {header.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          </Table>

          {/* BODY TABLE inside a scrollable Box */}
          <Box sx={{ maxHeight: bodyScrollHeight, overflowY: "auto" }}>
            <Table
              aria-label="collapsible table body"
              size="small"
              sx={{ tableLayout: "fixed", width: "100%" }}
            >
              <colgroup>
                {tableheaders.map((h) => (
                  <col key={h.id} style={{ minWidth: h.minWidth ?? 120 }} />
                ))}
              </colgroup>

              <TableBody>
                {sortedRows?.length > 0 && sortedRows ? (
                  sortedRows?.map((row: any, rowIndex: any) => (
                    <React.Fragment key={rowIndex}>
                      <TableRow
                        sx={{
                          "& > *": {
                            borderBottom:
                              "1px solid rgba(224, 224, 224, 1) !important",
                          },
                        }}
                      >
                        {headCells.map(([key], cellIndex) => (
                          <TableCell
                            key={cellIndex}
                            align="left"
                            sx={{
                              verticalAlign: "middle",
                              padding: "3px 16px !important",
                            }}
                          >
                            {key === "spend" ||
                            key === "CAC" ||
                            key === "cpc" ? (
                              <Box
                                sx={{
                                  ...getHighlightStyle(key, row[key], rowIndex),
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {formatValue(row[key]) !== ""
                                  ? formatCurrency(row[key])
                                  : ""}
                              </Box>
                            ) : key === "ftd" ? (
                              <Box
                                sx={{
                                  ...getHighlightStyle(key, row[key], rowIndex),
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {formatValue(row[key]) !== ""
                                  ? formatDecimal(row[key])
                                  : ""}
                              </Box>
                            ) : key === "clicks" ? (
                              <Box
                                sx={{
                                  borderRadius: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "left",
                                  width: "fit-content",
                                }}
                              >
                                {formatValue(row[key]) !== ""
                                  ? Math.round(row[key])
                                  : ""}
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  borderRadius: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "left",
                                  width: "fit-content",
                                }}
                              >
                                {formatValue(row[key])}
                              </Box>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    <TableCell colSpan={headCells.length} align="center">
                      <Typography variant="body1">
                        No data to Display
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </TableContainer>
      </Paper>
    </>
  );
};

export default NewSearchTerms;
